/**
 * Runtime configuration.
 *
 * Build-time values come from Vite (`.env.<mode>`); the terminal codes come from the **query string**,
 * because one deployed bundle serves every kiosk — each display differs only by which terminals it
 * watches, so that belongs in the URL rather than in a per-site build.
 */
export interface PortalConfig {
  /** STS origin, used only to resolve employee photo URLs. */
  stsApiUrl: string;
  attendanceHubUrl: string;
  /** BioTerminal codes this board displays. Never empty — callers must check `hasCodes` first. */
  codes: string[];
  /** How long a punch stays on screen before the stage goes blank again. */
  displayMs: number;
}

/** Query-string key holding one or more comma-separated terminal codes. */
export const CODES_PARAM = 'codes';

function required(value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(`Missing required configuration: ${name}`);
  }
  return value;
}

/**
 * Accepts `?codes=A,B`, repeated `?codes=A&codes=B`, and the singular `?code=A`, because kiosk URLs
 * get assembled by hand and a silently-empty board is a miserable thing to debug on a wall.
 */
export function readCodes(search: string = window.location.search): string[] {
  const params = new URLSearchParams(search);
  const raw = [...params.getAll(CODES_PARAM), ...params.getAll('code')];

  return [
    ...new Set(
      raw
        .flatMap((value) => value.split(/[,;\s]+/))
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean),
    ),
  ];
}

export function loadConfig(): PortalConfig {
  const env = import.meta.env;

  return {
    stsApiUrl: required(env.VITE_STS_API_URL, 'VITE_STS_API_URL').replace(/\/$/, ''),
    attendanceHubUrl: required(env.VITE_ATTENDANCE_HUB_URL, 'VITE_ATTENDANCE_HUB_URL'),
    codes: readCodes(),
    displayMs: Number(env.VITE_DISPLAY_SECONDS ?? 120) * 1000,
  };
}
